import React, { useEffect, useState, useMemo, useRef, Suspense } from 'react';
import {
  Layout,
  Card,
  Modal,
  Row,
  Col,
  Switch,
  Input,
  Form,
  Button,
} from 'antd';
import Sidebar from './components/Sidebar/Sidebar';
import { Label } from 'reactstrap';
import httpInjectorService from 'services/http-injector.service';
import { LinkIcon, AttachmentIcon } from '@chakra-ui/icons';
import './Confluence.css';
import { Route, Routes, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ActiveSpace from './components/ActiveSpace';
import TemplateList from './components/templatesComponents/TemplateList';
import PageContent from './components/page/Page';
import VersionHistory from './components/versionHistory/VersionHistory';
import CompareHistory from './components/versionHistory/components/CompareHistory';
import StarredPages from './components/Starred/StarredPages';
import ListTrashContent from './components/trashContent/ListTrashContent';
import Board from './components/whiteboard/Board';
import DatabaseTable from './components/database/DatabaseTable';
const { Content, Sider } = Layout;
const Spaces = React.lazy(() => import('./components/Spaces'));

export default function Confluence() {
  const [sidebarWidth, setSidebarWidth] = useState(260);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [slide, setSlide] = useState(1);
  const [spaces, setSpaces] = useState([]);

  const [selectedTab, setSelectedTab] = useState('all-spaces');

  const [icons, setIcons] = useState([]);
  const [activeIcon, setActiveIcon] = useState(null);
  const [showAllIcons, setShowAllIcons] = useState(false);
  const [visibleIcons, setVisibleIcons] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [yourSpaces, setYourSpaces] = useState([]);

  const { '*': wildcard } = useParams();
  const spaceid = wildcard?.split('/')[1] || null;
  const wrapperRef = useRef(null);
  const startX = useRef(0);
  const startWidth = useRef(0);
  const resizing = useRef(false);
  const frame = useRef(null);
  useEffect(() => {
    setVisibleIcons(showAllIcons ? icons : icons.slice(0, 5));
  }, [showAllIcons, icons]);

  const [activePurpose, setActivePurpose] = useState({
    id: 1,
    title: 'Work Hub',
    description:
      'A central place for teams to collaborate, exchange ideas, and refine work efficiently.',
    type: 'work_hub',
  });

  const [featuresList, setFeaturesList] = useState([
    {
      id: 1,
      name: 'Folders',
      enabled: true,
      icon: <AttachmentIcon fontSize={15} />,
    },
    { id: 2, name: 'Docs', enabled: false, icon: <LinkIcon /> },
    { id: 3, name: 'Whiteboards', enabled: false, icon: <LinkIcon /> },
    { id: 4, name: 'Databases', enabled: false, icon: <LinkIcon /> },
    { id: 5, name: 'Smart Links', enabled: false, icon: <LinkIcon /> },
    { id: 6, name: 'Blogs', enabled: false, icon: <LinkIcon /> },
  ]);

  const [spacePurposes] = useState([
    {
      id: 1,
      title: 'Work Hub',
      description:
        'A central place for teams to collaborate, exchange ideas, and refine work efficiently.',
      type: 'work_hub',
    },
    {
      id: 2,
      title: 'Knowledge Hub',
      description:
        'A shared space to create, organize, and access essential documents and knowledge.',
      type: 'knowledge_hub',
    },
    {
      id: 3,
      title: 'Custom Workspace',
      description:
        'Manually configure and tailor your space for specialized use cases and custom workflows.',
      type: 'custom',
    },
  ]);

  const [form] = Form.useForm();

  useEffect(() => {
    const fetchIcons = async () => {
      try {
        const response = await httpInjectorService.getIcons();

        if (response?.status === 'success' && response?.data?.length > 0) {
          setIcons(response.data);
          setActiveIcon(response.data[0]);
        }
      } catch (error) {
        console.error('Error fetching icons:', error);
      }
    };

    fetchIcons();
  }, []);

  const resetForm = () => {
    form.resetFields();

    setActivePurpose(spacePurposes[0]);
    setActiveIcon(icons?.[0] || null);

    setFeaturesList([
      {
        id: 1,
        name: 'Folders',
        enabled: true,
        icon: <AttachmentIcon fontSize={15} />,
      },
      { id: 2, name: 'Live docs', enabled: false, icon: <LinkIcon /> },
      { id: 3, name: 'Whiteboards', enabled: false, icon: <LinkIcon /> },
      { id: 4, name: 'Databases', enabled: false, icon: <LinkIcon /> },
      { id: 5, name: 'Smart Links', enabled: false, icon: <LinkIcon /> },
      { id: 6, name: 'Blogs', enabled: false, icon: <LinkIcon /> },
    ]);

    setSlide(1);
    setIsModalOpen(false);
  };

  const getSpaces = async () => {
    try {
      const response = await httpInjectorService.getSpaces({
        filter_type: 'All_Spaces',
        category_id: selectedCategory || null,
      });
      if (response.status === 'success') {
        setSpaces(response.data);
      } else {
        setSpaces([]);
      }
    } catch (error) {
      console.error('Error fetching spaces:', error);
      setSpaces([]);
    }
  };

  const getYourSpaces = async () => {
    try {
      const response = await httpInjectorService.getYourSpaces();
      if (response.status === 'success') {
        setYourSpaces(response.data);
      }
    } catch (error) {
      console.error('Error fetching your spaces:', error);
    }
  };

  const getTrashedSpaces = async () => {
    try {
      const response = await httpInjectorService.getTrashedSpaces();
      if (response.status === 'success') {
        setSpaces(response.data);
      }
    } catch (error) {
      console.error('Error fetching trashed spaces:', error);
    }
  };

  useEffect(() => {
    if (selectedTab === 'all-spaces') {
      getSpaces();
    } else if (selectedTab === 'trashed-spaces') {
      getTrashedSpaces();
    }
  }, [selectedCategory, selectedTab]);

  const handleSubmit = async (values) => {
    const reqBody = {
      space_name: values.space_name,
      space_type: activePurpose.type,
      description: activePurpose.description,
      categories: [activePurpose.id],
      space_icon_id: activeIcon?.id || null,

      blogs: featuresList.find((f) => f.name === 'Blogs')?.enabled ? 1 : 0,
      live_docs: featuresList.find((f) => f.name === 'Live docs')?.enabled
        ? 1
        : 0,
      whiteboards: featuresList.find((f) => f.name === 'Whiteboards')?.enabled
        ? 1
        : 0,
      databases_enabled: featuresList.find((f) => f.name === 'Databases')
        ?.enabled
        ? 1
        : 0,
      smart_links: featuresList.find((f) => f.name === 'Smart Links')?.enabled
        ? 1
        : 0,
      folders: featuresList.find((f) => f.name === 'Folders')?.enabled ? 1 : 0,

      is_default: 0,
    };

    // console.log('Submit Body:', reqBody);

    try {
      const response = await httpInjectorService.createSpace(reqBody);
      // console.log('Create Space Response:', response);
      toast.success(response.message || 'Space Created succesfully', {
        autoClose: 1000,
        position: 'top-right',
      });
      await getSpaces();
      await getYourSpaces();
    } catch (error) {
      console.error('Error creating space:', error);
    }

    resetForm();
  };

  const startResize = (e) => {
    if (!wrapperRef.current) return;

    resizing.current = true;
    startX.current = e.clientX;
    startWidth.current = wrapperRef.current.offsetWidth;

    wrapperRef.current.style.transition = 'none';

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    document.addEventListener('mousemove', resize);
    document.addEventListener('mouseup', stopResize);
  };

  const resize = (e) => {
    if (!resizing.current || !wrapperRef.current) return;

    if (frame.current) cancelAnimationFrame(frame.current);

    frame.current = requestAnimationFrame(() => {
      const delta = e.clientX - startX.current;
      const newWidth = startWidth.current + delta;

      if (newWidth >= 200 && newWidth <= 420) {
        setSidebarWidth(newWidth);
      }
    });
  };

  const stopResize = () => {
    if (!wrapperRef.current) return;

    resizing.current = false;

    const finalWidth = wrapperRef.current.offsetWidth;
    setSidebarWidth(finalWidth);

    wrapperRef.current.style.transition = 'width 140ms ease-out';

    document.body.style.userSelect = 'auto';
    document.body.style.cursor = 'default';

    document.removeEventListener('mousemove', resize);
    document.removeEventListener('mouseup', stopResize);
  };

  const spaceName = Form.useWatch('space_name', form);

  return (
    <Layout style={{ background: 'transparent' }}>
      <Sider
        ref={wrapperRef}
        width={sidebarWidth}
        style={{
          background: '#f7f8fa',
          borderRight: '1px solid #e0e0e0',
          position: 'fixed',
          left: 60,
          top: 50,
          bottom: 0,
          overflow: 'hidden',
          zIndex: 100,
        }}
      >
        <Sidebar
          setModal={setIsModalOpen}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          spaceid={spaceid}
        />
        <div className="resize-handle" onMouseDown={startResize} />
      </Sider>

      <Layout
        style={{
          marginLeft: sidebarWidth,
          transition: resizing.current ? 'none' : 'margin-left 0.15s ease',
          paddingLeft: 10,
          paddingTop: 50,
          background: 'transparent',
          marginBottom: 10,
        }}
      >
        <div style={{ flex: 1 }}>
          <Routes>
            <Route
              index
              path="*"
              element={
                <Suspense fallback={<div>Loading Spaces...</div>}>
                  <Spaces
                    spacePurposes={spacePurposes}
                    spaces={spaces}
                    setSpaces={setSpaces}
                    setSelectedCategory={setSelectedCategory}
                    setSelectedTab={setSelectedTab}
                    getSpaces={getSpaces}
                    yourSpaces={yourSpaces}
                    setYourSpaces={setYourSpaces}
                  />
                </Suspense>
              }
            />
            <Route path="space/:spaceid" element={<ActiveSpace />} />
            <Route
              path="space/:spaceid/page/:pageid"
              element={<PageContent />}
            />
            <Route
              path="space/:spaceid/page/:pageid/version-history"
              element={<VersionHistory />}
            />
            <Route
              path="space/:spaceid/page/:pageid/version-history/compare"
              element={<CompareHistory />}
            />
            <Route
              path="space/:spaceid/whiteboard/:boardid"
              element={<Board />}
            />
            <Route path='space/:spaceid/database/:databaseid' element={<DatabaseTable/>}/>
            <Route path="template" element={<TemplateList />} />
            <Route path="space/:spaceid/starred" element={<StarredPages />} />
            <Route path="space/:spaceid/trash" element={<ListTrashContent />} />
          </Routes>
        </div>
      </Layout>

      <Modal
        open={isModalOpen}
        onCancel={resetForm}
        width="65%"
        footer={null}
        style={{ top: 30 }}
      >
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <div style={{ padding: 10 }}>
            <h2>Create new space</h2>

            {slide === 1 && (
              <>
                <Row gutter={20}>
                  <Col span={12}>
                    <Form.Item
                      label="Name of the space"
                      name="space_name"
                      rules={[
                        { required: true, message: 'Enter space name' },
                        {
                          validator: (_, value) => {
                            if (!value || !value.trim()) {
                              return Promise.reject(
                                'Space name cannot be empty or spaces',
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input placeholder="Enter space name" />
                    </Form.Item>

                    <Label>
                      <b>Choose Icon</b>
                    </Label>

                    <Row gutter={[10, 10]}>
                      {visibleIcons.map((icon) => (
                        <Col key={icon.id} span={4}>
                          <div
                            onClick={() => setActiveIcon(icon)}
                            style={{
                              width: 60,
                              height: 60,
                              borderRadius: 6,
                              cursor: 'pointer',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              border:
                                activeIcon?.id === icon.id
                                  ? '3px solid #1677ff'
                                  : '1px solid #ddd',
                            }}
                          >
                            <img
                              src={icon.space_icon_url}
                              style={{ height: 40 }}
                            />
                          </div>
                        </Col>
                      ))}

                      {icons.length > 5 && (
                        <Col span={5}>
                          <div
                            onClick={() => setShowAllIcons(!showAllIcons)}
                            style={{
                              width: 60,
                              height: 60,
                              borderRadius: 6,
                              cursor: 'pointer',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              border: '1px dashed #1677ff',
                              color: '#1677ff',
                              fontSize: 12,
                              textAlign: 'center',
                            }}
                          >
                            {showAllIcons
                              ? 'Less'
                              : `+${icons.length - 5} more`}
                          </div>
                        </Col>
                      )}
                    </Row>
                  </Col>

                  <Col span={12}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        height: '100%',
                        paddingTop: 20,
                      }}
                    >
                      <img
                        src="https://cdn-icons-png.flaticon.com/512/7158/7158955.png"
                        style={{ height: 260 }}
                      />
                    </div>
                  </Col>
                </Row>

                <div style={{ marginTop: 8 }}>
                  <Label>
                    <b>Choose Purpose</b>
                  </Label>

                  <Row gutter={12}>
                    {spacePurposes.map((purpose) => (
                      <Col span={8} key={purpose.id}>
                        <div
                          onClick={() => setActivePurpose(purpose)}
                          style={{
                            padding: 12,
                            borderRadius: 6,
                            cursor: 'pointer',
                            border:
                              activePurpose?.id === purpose.id
                                ? '2px solid purple'
                                : '1px solid #ddd',
                            height: '100%',
                          }}
                        >
                          <h4>{purpose.title}</h4>
                          <p style={{ fontSize: 12 }}>{purpose.description}</p>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              </>
            )}

            {slide === 2 && (
              <Row gutter={20}>
                <Col span={12}>
                  <Form.Item
                    label="Name of the space"
                    name="space_name"
                    rules={[
                      { required: true, message: 'Enter space name' },
                      {
                        validator: (_, value) => {
                          if (!value || !value.trim()) {
                            return Promise.reject(
                              'Space name cannot be empty or spaces',
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>

                  <Label>
                    <b>Features</b>
                  </Label>

                  <Card>
                    {featuresList.map((feature) => (
                      <Row
                        key={feature.id}
                        style={{
                          padding: 10,
                          borderBottom: '1px solid #eee',
                          alignItems: 'center',
                        }}
                      >
                        <Col span={3}>{feature.icon}</Col>
                        <Col span={15}>{feature.name}</Col>
                        <Col span={6}>
                          <Switch
                            checked={feature.enabled}
                            onChange={(checked) =>
                              setFeaturesList((prev) =>
                                prev.map((f) =>
                                  f.id === feature.id
                                    ? { ...f, enabled: checked }
                                    : f,
                                ),
                              )
                            }
                          />
                        </Col>
                      </Row>
                    ))}
                  </Card>
                </Col>

                <Col span={12}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                    }}
                  >
                    <img
                      src="https://cdn-icons-png.flaticon.com/512/7158/7158955.png"
                      style={{ height: 300 }}
                    />
                  </div>
                </Col>
              </Row>
            )}
          </div>

          <Row justify="space-between" style={{ marginTop: 20 }}>
            <div>Step {slide} of 2</div>

            <div>
              {slide === 2 && (
                <Button onClick={() => setSlide(1)} style={{ marginRight: 10 }}>
                  Previous
                </Button>
              )}

              {slide === 1 && (
                <Button
                  style={{ background: '#722ed1', color: '#fff' }}
                  onClick={() => setSlide(2)}
                >
                  Next
                </Button>
              )}

              {slide === 2 && (
                <Button
                  style={{ background: '#722ed1', color: '#fff' }}
                  htmlType="submit"
                  disabled={!spaceName || !spaceName.trim()}
                >
                  Create Space
                </Button>
              )}
            </div>
          </Row>
        </Form>
      </Modal>
    </Layout>
  );
}
